import React, { useContext, useState, useEffect, useMemo } from "react";
import "../styles/BackgroundVideo.css";
import { CartContext } from "../contexts/CartContext.js";
import ProductColors from "./ProductColors.js";
import "../styles/Card.css";
import { Link, useNavigate } from "react-router-dom";
import BadgeList from "./BadgeList.js";
import { Send } from "lucide-react";
import { emailListing } from "../apis/apiBaltee.js";
import SizeGuide from "./SizeGuide.js";

const ProductCard = ({
  products,
  forcedColor,
  productFloat = false,
  position = "bottom",
  imgPosition = "vertical",
  backgroundVideoEnabled = true,
  modeColor = "white",
  discover = true,
  actions = true,
  payment = true,
  onProductSelected,
  hasEmail = false,
  sr = false,
  newProduct = false,
  cred = false,
  productIcon = true,
}) => {
  const { addToCart, cartItems } = useContext(CartContext);
  const [mode, setMode] = useState(modeColor);
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [submitForm, setSubmitForm] = useState(!!localStorage.getItem("h01Subscription"));
  const [submitFormStock, setSubmitFormStock] = useState(!!localStorage.getItem("stock"));

  // Ajout d'un state pour la taille sélectionnée
  const [selectedSize, setSelectedSize] = useState("");

  // Sélection de la couleur (par défaut colorOptions[1] si dispo)
  const colorOptions = (products?.data || []).map((p) => p.metadata.color);
  const [selectedColor, setSelectedColor] = useState(colorOptions[0] || null);

  const [showPopup, setShowPopup] = useState(false);

  const handleOpen = () => setShowPopup(true);
  const handleClose = () => setShowPopup(false);

  useEffect(() => {
    if (forcedColor && forcedColor !== selectedColor) {
      setSelectedColor(forcedColor);
    }
  }, [forcedColor, selectedColor]);

  const handleColorChange = (color) => {
    setSelectedColor(color);
    const foundProduct = products?.data?.find(
      (p) => p.metadata.color === color
    );
    if (onProductSelected && foundProduct) {
      onProductSelected(foundProduct);
    }
  };

  // Produit courant en fonction de la couleur sélectionnée
  const currentProduct = products?.data?.find(
    (p) => p.metadata.color === selectedColor
  );

  // On mémorise le tableau des tailles pour éviter l’avertissement ESLint
  // et pour éviter de recréer un tableau à chaque rendu
  const sizes = useMemo(() => {
    if (!currentProduct?.metadata?.size) return [];
    return currentProduct.metadata.size.split(";");
  }, [currentProduct]);

  // Si le produit / la liste de tailles change, on force la première taille par défaut
  useEffect(() => {
    if (sizes.length > 0) {
      setSelectedSize(sizes[0]); // première taille par défaut
    } else {
      setSelectedSize("");
    }
  }, [sizes]);

  const productImage = 
    "https://www.balteemobility.com/ecommerce-cache-assets/stripe/images/products/" +
    currentProduct.productId;
  const backgroundVideo = products.bgVideo;

  const subtitle = currentProduct?.metadata?.subtitle ?? "";
  const sentence1 = currentProduct?.metadata?.sentence_1 ?? "";
  const sentence2 = currentProduct?.metadata?.sentence_2 ?? "";
  const promoDate = currentProduct?.metadata?.promo_date ?? "";
  const outOfStock = currentProduct?.metadata?.out_of_stock ?? "";
  const outOfStockS = currentProduct?.metadata?.out_of_stock_S ?? "";
  const outOfStockL = currentProduct?.metadata?.out_of_stock_L ?? "";
  const lignes = sentence1.split(" - ");
  const badges = currentProduct?.metadata?.badges ?? "";
  const badgeArray = badges
    .split(";")
    .map((item) => item.trim())
    .filter((item) => item !== "");

  const priceOneTime = currentProduct?.price?.data?.find(
    (price) => price.type === "one_time"
  );
  // const priceRecurring = currentProduct?.price?.data?.find(
  //   (price) => price.type === "recurring"
  // );

  const buyPrice = priceOneTime ? priceOneTime.unit_amount / 100 : 0;
  // const rentPrice = priceRecurring ? priceRecurring.unit_amount / 100 : 0;
  const InitBuyPrice = priceOneTime?.metadata?.init_price
    ? parseFloat(priceOneTime?.metadata?.init_price.replace(",", "."))
    : null;

  const handleSubmit = (e) => {
    e.preventDefault();
    console.log("Email soumis:", email);
    emailListing({ email: email, source: "H01" });
    setSubmitForm(true);
    localStorage.setItem("h01Subscription", true);
  };

  const handleSubmitStock = (e) => {
    e.preventDefault();
    console.log("Email soumis:", email);
    emailListing({ email: email, source: "stock "+currentProduct?.name });
    setSubmitFormStock(true);
    localStorage.setItem("stock", true);
  };

  const transformProduct = (product, type) => {
    if (!product?.price?.data) {
      return product;
    }
    const oneTimePrice = product.price.data.find(
      (priceItem) => priceItem.type === type
    );
    if (!oneTimePrice) {
      return product;
    }
    return {
      ...product,
      price: oneTimePrice,
    };
  };

  const buy = (product) => {
    const productTransformed = transformProduct(product, "one_time");
    const newId =
      cartItems && cartItems.length > 0
        ? Math.max(...cartItems.map((item) => item.id)) + 1
        : 0;

    productTransformed.id = newId;

    // Ajout de la taille sélectionnée au produit
    if (selectedSize) {
      productTransformed.metadata = {
        ...productTransformed.metadata,
        selectedSize: selectedSize,
      };
    }

    addToCart(productTransformed);
    navigate("/checkout/cart");
  };

  // const rent = (product) => {
  //   const productTransformed = transformProduct(product, "recurring");
  //   // ...
  // };

  return (
    <div className="background-video-container">
      {newProduct && <p className="new">Nouveau !</p>}

      {backgroundVideoEnabled && (
        <video
          autoPlay
          loop
          muted
          playsInline
          className="background-video"
          style={{ opacity: 0.9 }}
          onError={() => setMode("blue")}
        >
          <source src={backgroundVideo} type="video/mp4" />
          Your browser does not support the video tag.
        </video>
      )}

      <div className="content">
        {sr && (
          <img
            src="/assets/other/SR-white.jpeg"
            alt="sécurité routière"
            className="CartProduct_product-SR"
          />
        )}

        <div className="product-info">
          {position === "top" && (
            <div style={{ marginTop: 30 }} className="product-image">
              <img
                src={productImage}
                alt={currentProduct?.name}
                className={`product ${productFloat && "product-float"} ${
                  imgPosition === "horizontal" && "horizontal"
                }`}
                style={{
                  width:
                    currentProduct.metadata.ref !== "H01" ? "50%" : "90%",
                }}
              />
              {colorOptions.length > 1 && (
                <ProductColors
                  colors={colorOptions}
                  selectedColor={selectedColor}
                  onSelectColor={handleColorChange}
                />
              )}
            </div>
          )}

          <div className="product-info">
            <div className="title-with-logo">
              <h1
                className="product-title"
                style={{
                  color: backgroundVideoEnabled
                    ? mode === "white"
                      ? "#fff"
                      : "#000"
                    : "#000",
                  margin: "0 10px 0 10px",
                }}
                dangerouslySetInnerHTML={{ __html: currentProduct?.name }}
              ></h1>
              {subtitle && (
                <h2
                  style={{
                    marginTop: 0,
                    marginBottom: 0,
                    color: mode === "white" ? "#fff" : (currentProduct?.metadata.ref == "B01LITE"? "#4FB897":"#000"),
                  }}
                >
                  {subtitle}
                </h2>
              )}
            </div>

            <BadgeList badges={badgeArray} mode={mode} />

            <div style={{ marginLeft: 10, marginRight: 10 }}>
              {lignes.map((ligne, index) => (
                <div key={index}>
                  <small
                    style={{
                      color: backgroundVideoEnabled
                        ? mode === "white"
                          ? "#fff"
                          : "#000"
                        : "#000",
                      fontSize: 16,
                    }}
                  >
                    {ligne}
                  </small>
                </div>
              ))}
            </div>

            <p
              className="product-subtitle"
              style={{
                color: backgroundVideoEnabled
                  ? mode === "white"
                    ? "#fff"
                    : "#000"
                  : "#000",
              }}
            >
              {sentence2}
            </p>

            {productIcon ? <div>
              <img alt="product icons" style={{width: currentProduct.metadata.ref === "H01" ? '45%' : '60%', margin: "0 auto", opacity: 0.5}} src={currentProduct.metadata.ref === "H01" ? "/assets/other/h01-icons.png" : currentProduct.metadata.ref === "B01" || currentProduct.metadata.ref === "B01LITE" ? "/assets/other/b01-icons.png" : null}/>
            </div> : null}

            <div className="product-buttons">
              {discover && (
                <button
                  className="btn learn-more"
                  style={{
                    backgroundColor: mode === "white" ? "#fff" : "#fff",
                    color: mode === "white" ? "#000" : "#4FB897",
                    borderColor: mode === "white" ? "#FFF" : "#4fb897",
                    fontWeight: 'bold',
                    borderWidth: 3
                  }}
                  onClick={() =>
                    navigate(
                      currentProduct.metadata.ref === "B01" || currentProduct.metadata.ref === "B01LITE"
                        ? (currentProduct.metadata.ref === "B01LITE" ? `/product/boitier-multifonction-securite-velo-trottinette-b01-lite/${currentProduct.metadata.color}`: `/product/boitier-multifonction-securite-velo-trottinette-b01/${currentProduct.metadata.color}`)
                        : `/product/casque-intelligent-lumineux-h01/${currentProduct.metadata.color}`
                    )
                  }
                >
                  En savoir +
                </button>
              )}
            </div>

            {position === "bottom" && (
              <div style={{ marginTop: 30 }} className="product-image">
                <img
                  src={productImage}
                  alt={currentProduct?.name}
                  className={`product ${productFloat && "product-float"} ${
                    imgPosition === "horizontal" && "horizontal"
                  }`}
                  style={{
                    width:
                      currentProduct.metadata.ref !== "H01" ? "50%" : "90%",
                  }}
                />
                {colorOptions.length > 1 && (
                  <ProductColors
                    colors={colorOptions}
                    selectedColor={selectedColor}
                    onSelectColor={handleColorChange}
                  />
                )}
              </div>
            )}

            {buyPrice ? (
              <div
                style={{
                  borderTop: `1px solid ${
                    mode === "white"
                      ? "rgba(255, 255, 255, 0.5)"
                      : "rgba(0, 0, 0, 0.2)"
                  }`,
                  borderBottom: `1px solid ${
                    mode === "white"
                      ? "rgba(255, 255, 255, 0.5)"
                      : "rgba(0, 0, 0, 0.2)"
                  }`,
                  width: "80%",
                  margin: "20px auto",
                  marginTop: position === "bottom" ? 0 : 20,
                  display: "flex",
                  flexDirection: "row",
                  gap: 20,
                  alignItems: "center",
                  justifyContent: "center",
                  paddingTop: 10,
                  paddingBottom: 15,
                }}
              >
                <div style={{ display: "flex", flexDirection: "column" }}>
                  <div>
                  {InitBuyPrice && promoDate ?<p style={{fontWeight: 'bold', color: '#FFF', backgroundColor: "#333", padding: '10px 15px', borderRadius: 50}}>Promotion jusqu'au {promoDate}</p> : null}

                    {InitBuyPrice && (
                      <p
                        style={{
                          color: mode === "white" ? "#FFF" : "#333",
                          opacity: 0.8,
                          margin: 0,
                        }}
                      >
                        <span style={{ textDecorationLine: "<lin>e-through" }}>
                          {InitBuyPrice}&nbsp;€&nbsp; 
                        </span>
                        &nbsp; 
                        <span style={{fontWeight: 'bold', color: "#4FB897" }}>
                        -{(InitBuyPrice-buyPrice).toFixed(2)}&nbsp;€&nbsp; 
                        </span>
                      </p>
                    )}

                    <p
                      style={{
                        color: mode === "white" ? "#FFF" : "#333",
                        margin: 0,
                        fontWeight: 900,
                        fontSize: 20,
                      }}
                    >
                      {buyPrice} € 
                    </p>

                    {sizes.length && actions > 0 ? (
                      <div style={{ marginTop: 10, marginBottom: 10 }}>
                        <select
                          style={{ padding: "11px 15px", marginRight: 10, borderRadius: 28, minHeight: 30, minWidth: 70, fontSize: 16, color: "#4FB987" }}
                          value={selectedSize}
                          onChange={(e) => setSelectedSize(e.target.value)}
                        >
                          {sizes.map((size, idx) => (
                            <option key={idx} value={size}>
                              {size}
                            </option>
                          ))}
                        </select>
                        <Link style={{color: '#000', fontSize: 14, marginLeft: 10}}  onClick={handleOpen}>Guide des tailles</Link>
                      </div>
                    ) : null}

                    {actions && (
                      <button
                        className="btn buy"
                        style={{
                          borderColor: "#4fb897",
                          color: "#fff",
                          backgroundColor: "#4fb897",
                          marginTop: 10,
                          // fontWeight: 'bold',
                          fontSize: 20,
                        }}
                        onClick={() => buy(currentProduct)}
                        disabled={sizes.length > 0 && !selectedSize}
                      >
                        Ajouter au panier
                      </button>
                    )}
                  </div>

                  {(outOfStock || (outOfStockS && selectedSize === "S/M") || (outOfStockL  && selectedSize === "L/XL")) && !submitFormStock && (
                    <div>  
                      < br/>

                      < hr/>

                      <button
                        className="btn buy"
                        style={{
                          borderColor: "#f48466",
                          borderStyle: "dashed",
                          color: "#f48466",
                          backgroundColor: "#fff",
                          marginTop: 10,
                          fontSize: 16,
                        }}
                        disabled={true}
                      >
                        Victime de son succès
                      </button>
                      < br/>
                      <p style={{color: "#f48466"}}>Attention : Vous pouvez tout de même passer commande pour profiter de l'offre, la livraison se fera à partir du <strong> <strong>{outOfStock ? outOfStock : (outOfStockS ? outOfStockS : (outOfStockL ? outOfStockL : null))}</strong></strong>.</p>

                      < hr/>
                      <p style={{ margin: 0, color: "#333" }}>
                        Je souhaite être tenu au courant par mail.
                      </p>
                      <div className="email-form-container">
                        <form onSubmit={handleSubmitStock} className="email-form">
                          <input
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder="Entrez votre email"
                            className="email-input"
                            required
                          />
                          <button type="submit" className="send-button">
                            <Send size={24} />
                          </button>
                        </form>
                      </div>
                    </div>
                  )}

                  {(outOfStock || (outOfStockS && selectedSize === "S/M") || (outOfStockL  && selectedSize === "L/XL")) && submitFormStock && (
                      <div>
                        <button
                      className="btn buy"
                      style={{
                        borderColor: "#f48466",
                        borderStyle: "dashed",
                        color: "#f48466",
                        backgroundColor: "#fff",
                        marginTop: 10,
                        fontSize: 16,
                      }}
                      disabled={true}
                    >
                      Victime de son succès
                    </button>
                    < br/>
                    <p style={{color: "#f48466"}}>Attention : Vous pouvez tout de même passer commande pour profiter de l'offre, la livraison se fera à partir du <strong>{outOfStock ? outOfStock : (outOfStockS ? outOfStockS : (outOfStockL ? outOfStockL : null))}</strong>.</p>
                    <p style={{ margin: 0, color: "#000" }}>
                      Vous serez informés de la disponibilité par email.
                    </p>
                      </div>
                  )}

                  {cred && (
                    <div
                      style={{ color: mode === "white" ? "#000" : "#000" }}
                      className="cred"
                    >
                      <div>
                        <img
                          src={"/assets/other/SR-white.jpeg"}
                          alt={"sécurité routière"}
                        />
                      </div>
                      <div>
                        <img
                          src={"/assets/logos/lepine2.png"}
                          alt={"sécurité routière"}
                        />
                      </div>
                      <div>
                        <img
                          src={"/assets/logos/tv.png"}
                          alt={"sécurité routière"}
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ) : null}

            {payment && (
              <small>
                <p
                  style={{
                    color: mode === "white" ? "#FFF" : "#333",
                    marginInline: 15,
                    fontSize: 14,
                  }}
                >
                  <strong>3x sans frais</strong> via{" "}
                  <img
                    src="/assets/logos/klarna.jpg"
                    alt="klarna"
                    style={{
                      height: 25,
                      verticalAlign: "middle",
                      borderRadius: 5,
                      padding: 3,
                      backgroundColor: "#FFB5C9",
                    }}
                  />
                  &nbsp;| 3 x <strong>{(buyPrice / 3).toFixed(2)} €</strong>
                  <br />
                  <br />
                  <strong>4x sans frais</strong> via{" "}
                  <img
                    src="/assets/logos/paypal.png"
                    alt="paypal"
                    style={{
                      height: 25,
                      verticalAlign: "middle",
                      borderRadius: 5,
                      padding: 3,
                      backgroundColor: "#fff",
                    }}
                  />
                  &nbsp;| 4 x <strong>{(buyPrice / 4).toFixed(2)} €</strong>
                  <br />
                  <br />
                  <small style={{ opacity: 0.5 }}>
                    Paiement en 3 ou 4 fois à sélectionner au moment du paiement
                  </small>
                </p>
              </small>
            )}
          </div>

          {hasEmail && !submitForm && (
            <div>
              <p style={{ margin: 0, color: "white" }}>
                Je souhaite être tenu au courant de la sortie.
              </p>
              <div className="email-form-container">
                <form onSubmit={handleSubmit} className="email-form">
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Entrez votre email"
                    className="email-input"
                    required
                  />
                  <button type="submit" className="send-button">
                    <Send size={24} />
                  </button>
                </form>
              </div>
            </div>
          )}

          {hasEmail && submitForm && (
            <p style={{ margin: 0, color: "white" }}>
              Vous serez informés de la sortie par email.
            </p>
          )}
        </div>
      </div>
      <SizeGuide
        isOpen={showPopup}
        onClose={handleClose}
        imageUrl={"/assets/products/size.png"}
      />
    </div>
  );
};

export default ProductCard;
